import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronDown, ArrowRight } from "lucide-react";
import { EASE } from "../ui/Reveal";
import useLang from "../../hooks/useLang";

export const scrollToFlow = (navigate, pathname) => {
  if (pathname === "/") {
    document.getElementById("how-we-work")?.scrollIntoView({ behavior: "smooth", block: "start" });
  } else {
    navigate("/", { state: { scrollTo: "how-we-work" } });
  }
};

// Menu item that previews the four phases in a small panel instead of jumping to the section.
const HowWeWorkMenu = () => {
  const { t, isRTL } = useLang();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  const phases = t("howWeWork.steps", { returnObjects: true });
  const flow = t("howWeWork.flow.steps", { returnObjects: true });

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={boxRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="how-we-work-panel"
        className={`flex items-center gap-1.5 h-11 px-3 rounded-xl text-sm font-semibold transition-colors ${
          open ? "text-white bg-white/10" : "text-white/70 hover:text-white"
        }`}
      >
        {t("howWeWork.badge")}
        <ChevronDown size={15} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>

        {open && (
          <motion.div
            id="how-we-work-panel"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: EASE }}
            className="absolute end-0 top-[calc(100%+10px)] z-50 w-[min(92vw,560px)] rounded-2xl border border-white/10 bg-[#141414] p-5 shadow-2xl"
          >
            <div className="relative grid grid-cols-4 gap-3">
              <motion.div
                className={`absolute top-[15px] start-[12.5%] end-[12.5%] h-[2px] bg-gradient-to-r from-white/30 to-rose ${
                  isRTL ? "origin-right bg-gradient-to-l" : "origin-left"
                }`}
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
              />
              {phases.map((p, i) => (
                <motion.div
                  key={p.num}
                  className="relative text-center"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.15 + i * 0.12, ease: EASE }}
                >
                  <div
                    className={`mx-auto w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-extrabold ${
                      i === phases.length - 1 ? "bg-gradient-to-br from-rose-start to-rose text-white" : "bg-white text-ink"
                    }`}
                  >
                    {p.num}
                  </div>
                  <div className="font-mono text-[10px] tracking-[0.12em] text-rose-light mt-3">{p.phase}</div>
                  <div className="text-[13px] font-extrabold text-white mt-1 leading-snug">{p.title}</div>
                  <ul className="mt-2 space-y-1">
                    {flow
                      .filter((s) => s.phase === i)
                      .map((s) => (
                        <li key={s.title} className="text-[11.5px] text-white/50 leading-snug">
                          {s.title}
                        </li>
                      ))}
                  </ul>
                </motion.div>
              ))}
            </div>

            <div className="mt-5 pt-4 border-t border-white/10 flex flex-wrap items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  scrollToFlow(navigate, pathname);
                }}
                className="link-arrow group text-white hover:text-rose-light"
              >
                {t("howWeWork.flow.seeFull")}
                <ArrowRight
                  size={14}
                  className={`transition-transform duration-300 group-hover:translate-x-1 rtl:rotate-180`}
                />
              </button>
              <div className="flex items-center gap-4">
                <Link to="/projects" className="link-arrow text-white/70 hover:text-white">
                  {t("ourWork.viewAll")}
                </Link>
                <Link to="/contact" className="btn-rose !px-4 !py-2.5">
                  {t("cta.button")}
                </Link>
              </div>
            </div>
          </motion.div>
        )}
    </div>
  );
};

export default HowWeWorkMenu;
